import { useCallback, useEffect, useRef, useState } from "react";
import { Api, ApiError, type DriveFiles, type IntegrationState, type Source } from "../api.js";
import { useI18n } from "../i18n.js";
import { LIMITS } from "../draft.js";
import { pickDriveFiles } from "../picker.js";
import { Badge, Section } from "./ui.js";

const KNOWN = ["not_connected", "needs_reconnect", "held", "limit_reached", "too_soon", "forbidden", "not_enabled", "rate_limited"];
const RESULTS = ["connected", "denied", "missing_scope", "bad_state", "provider_failed", "held", "not_enabled"];

/** What the browser came back with from Google: `#/…?integration=google_drive&result=connected`. */
export function readReturn(hash: string): { provider: string; result: string } | null {
  const q = new URLSearchParams(hash.split("?")[1] ?? "");
  const provider = q.get("integration");
  const result = q.get("result");
  return provider && result ? { provider, result } : null;
}

/**
 * The current address without the trip-to-Google marks: `#/agents/ag?step=train&integration=google_drive&result=connected` becomes
 * `#/agents/ag?step=train`. The step stays, so a return from Google lands on the same agent and the same step.
 */
export function cleanHash(hash: string): string {
  const [path = "#/", query = ""] = hash.split("?");
  const q = new URLSearchParams(query);
  q.delete("integration");
  q.delete("result");
  const rest = q.toString();
  return rest ? `${path}?${rest}` : path || "#/";
}

/**
 * Google Drive in the Train step: connect, pick the files, see each one's state, disconnect (keeping or removing what was read).
 * Shows nothing at all while the connection is switched off on the server (the older "coming soon" row stays then).
 */
export function DrivePanel({ api, agentId, sources, refresh, state, reload }: { api: Api; agentId: string; sources: Source[]; refresh: () => Promise<void> | void; state: IntegrationState; reload: () => Promise<void> }) {
  const { t, lang } = useI18n();
  const [busy, setBusy] = useState<"" | "connect" | "pick" | "disconnect">("");
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [keepSynced, setKeepSynced] = useState(true);
  const [confirming, setConfirming] = useState(false);
  const [removeContent, setRemoveContent] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);
  const chooseRef = useRef<HTMLButtonElement>(null);
  const [focusChoose, setFocusChoose] = useState(false);

  const fail = useCallback(
    (e: unknown) => {
      const code = e instanceof ApiError ? e.code : "";
      setError(KNOWN.includes(code) ? t(`drive.err.${code}` as never) : e instanceof ApiError && e.status === 403 ? t("drive.err.forbidden") : t("drive.err.other"));
    },
    [t],
  );

  // Back from Google: say how it went once, then clean the address.
  useEffect(() => {
    const r = readReturn(location.hash);
    if (!r || r.provider !== "google_drive") return;
    if (r.result === "connected") setNotice(t("drive.result.connected"));
    else setError(RESULTS.includes(r.result) ? t(`drive.result.${r.result}` as never) : t("drive.err.other"));
    history.replaceState(null, "", location.pathname + location.search + cleanHash(location.hash));
    void reload().then(() => {
      // Land on the Drive section with the next thing to do in reach.
      sectionRef.current?.scrollIntoView?.({ block: "start" });
      setFocusChoose(r.result === "connected");
    });
  }, [reload, t]);
  useEffect(() => {
    if (focusChoose) chooseRef.current?.focus();
  }, [focusChoose]);

  const conn = state.connection;
  const start = async () => {
    setBusy("connect");
    setError(null);
    try {
      const { url } = await api.connectIntegration("google_drive", cleanHash(location.hash));
      location.assign(url);
    } catch (e) {
      fail(e);
      setBusy("");
    }
  };
  const pick = async () => {
    setBusy("pick");
    setError(null);
    setNotice(null);
    try {
      const cfg = await api.pickerToken("google_drive");
      const files = await pickDriveFiles(cfg, lang);
      if (files.length > LIMITS.drivePicksMax) {
        setError(t("drive.tooMany", { n: String(files.length), max: String(LIMITS.drivePicksMax) }));
      } else if (files.length) {
        await api.addSource(agentId, { type: "google_drive", files, keepSynced });
        setNotice(t("drive.added", { n: String(files.length) }));
        await refresh();
      }
    } catch (e) {
      fail(e);
    } finally {
      setBusy("");
    }
  };
  const disconnect = async () => {
    setBusy("disconnect");
    setError(null);
    try {
      await api.disconnectIntegration("google_drive", removeContent);
      setConfirming(false);
      setNotice(removeContent ? t("drive.disconnected.removed") : t("drive.disconnected.kept"));
      await reload();
      await refresh();
    } catch (e) {
      fail(e);
    } finally {
      setBusy("");
    }
  };
  const syncNow = async (s: Source) => {
    setError(null);
    try {
      await api.reindexSource(agentId, s.id);
      await refresh();
    } catch (e) {
      fail(e);
    }
  };
  const remove = async (s: Source) => {
    try {
      await api.removeSource(agentId, s.id);
      await refresh();
    } catch (e) {
      fail(e);
    }
  };

  if (!state.available && !conn) return null;
  return (
    <div ref={sectionRef}>
    <Section title={t("drive.title")} help={t("drive.help")}>
      {notice && <p role="status" className="notice">{notice}</p>}
      {error && <p role="alert" className="field-error">{error}</p>}

      {!conn && (
        <>
          <p className="hint">{t("drive.privacy")}</p>
          <button className="btn primary" disabled={busy !== "" || !state.available} onClick={() => void start()}>
            {busy === "connect" ? t("drive.opening") : t("drive.connect")}
          </button>
        </>
      )}

      {conn && (
        <>
          <p>
            <Badge tone={conn.status === "active" ? "ok" : "bad"}>{conn.status === "active" ? t("drive.status.active") : t("drive.status.needsReconnect")}</Badge>{" "}
            {conn.email ? t("drive.connectedAs", { email: conn.email }) : null}
          </p>
          {conn.status === "needs_reconnect" ? (
            <div className="banner warn" role="alert">
              <p>{t("drive.needsReconnect")}</p>
              <button className="btn primary" disabled={busy !== ""} onClick={() => void start()}>{t("drive.connectAgain")}</button>
            </div>
          ) : (
            <>
              <label className="inline">
                <input type="checkbox" checked={keepSynced} onChange={(e) => setKeepSynced(e.target.checked)} /> {t("drive.keepSynced")}
              </label>
              <p className="hint">{t("drive.sheets")}</p>
              <button ref={chooseRef} className="btn secondary" disabled={busy !== "" || state.held === true} onClick={() => void pick()}>
                {busy === "pick" ? t("drive.opening") : t("drive.choose")}
              </button>
            </>
          )}

          {sources.length > 0 && (
            <ul className="sources" aria-label={t("drive.sources")}>
              {sources.map((s) => (
                <DriveSource key={s.id} api={api} agentId={agentId} source={s} syncNow={syncNow} remove={remove} />
              ))}
            </ul>
          )}

          {!confirming ? (
            <button className="btn link" onClick={() => setConfirming(true)}>{t("drive.disconnect")}</button>
          ) : (
            <fieldset className="card" data-testid="drive-disconnect">
              <legend>{t("drive.disconnect.title")}</legend>
              <label className="inline"><input type="radio" name="drive-content" checked={!removeContent} onChange={() => setRemoveContent(false)} /> {t("drive.disconnect.keep")}</label>
              <label className="inline"><input type="radio" name="drive-content" checked={removeContent} onChange={() => setRemoveContent(true)} /> {t("drive.disconnect.remove")}</label>
              <div className="row">
                <button className="btn danger" disabled={busy !== ""} onClick={() => void disconnect()}>{t("drive.disconnect.confirm")}</button>
                <button className="btn secondary" onClick={() => setConfirming(false)}>{t("drive.cancel")}</button>
              </div>
            </fieldset>
          )}
        </>
      )}
    </Section>
    </div>
  );
}

const FILE_TONE: Record<string, "ok" | "info" | "warn" | "bad" | "muted"> = { indexed: "ok", pending: "info", not_found: "warn", no_access: "warn", unsupported: "muted", failed: "bad" };

export function DriveSource({ api, agentId, source, syncNow, remove }: { api: Api; agentId: string; source: Source; syncNow: (s: Source) => void; remove: (s: Source) => void }) {
  const { t } = useI18n();
  const [files, setFiles] = useState<DriveFiles | null>(null);
  const [open, setOpen] = useState(false);
  const toggle = async () => {
    setOpen(!open);
    if (!open) setFiles(await api.driveFiles(agentId, source.id).catch(() => null));
  };
  // While the files are being read, show how far it is: "Imported 37 of 150 files". Re-read each time the source list refreshes.
  const reading = source.status === "pending" || source.status === "indexing";
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  useEffect(() => {
    let live = true;
    if (!reading && !(source.status === "indexed" || source.status === "error")) return;
    void api.driveFiles(agentId, source.id).then(
      (r) => {
        if (!live) return;
        // A folder lists only what could not be read, plus how many are in the knowledge.
        if (r.indexedCount !== undefined) setProgress({ done: r.indexedCount, total: r.indexedCount + r.files.length });
        else setProgress({ done: r.files.filter((f) => f.status === "indexed").length, total: r.files.length });
        if (open) setFiles(r);
      },
      () => {},
    );
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, agentId, source.id, source.status, source.chunks, source.sync?.lastRunAt]);
  const last = source.sync?.lastRunAt ? new Date(source.sync.lastRunAt).toLocaleString([], { dateStyle: "medium", timeStyle: "short" }) : null;
  return (
    <li data-status={source.status}>
      <div className="row between">
        <strong>{source.name}</strong>
        <Badge tone={source.status === "indexed" ? "ok" : source.status === "error" ? "bad" : "info"}>{t(`drive.source.${source.status}` as never)}</Badge>
      </div>
      {source.error && <p className="hint">{source.error}</p>}
      {progress && progress.total > 0 && (
        <p className="hint" role="status" data-testid="drive-progress">
          {reading ? t("drive.progress", { done: String(progress.done), total: String(progress.total) }) : progress.done < progress.total ? t("drive.progress.some", { done: String(progress.done), total: String(progress.total) }) : t("drive.progress.all", { total: String(progress.total) })}
        </p>
      )}
      <p className="hint">{source.sync ? (last ? t("drive.lastSync", { when: last }) : t("drive.neverSynced")) : t("drive.importedOnce")}</p>
      <div className="row">
        <button className="btn secondary" onClick={() => syncNow(source)}>{t("drive.syncNow")}</button>
        <button className="btn secondary" aria-expanded={open} onClick={() => void toggle()}>{open ? t("drive.hideFiles") : t("drive.showFiles")}</button>
        <button className="btn link" aria-label={t("drive.removeSource", { name: source.name })} onClick={() => remove(source)}>{t("drive.remove")}</button>
      </div>
      {open && files && (
        <ul className="files" aria-label={t("drive.files")}>
          {files.files.map((f) => (
            <li key={f.id}>
              {f.name} <Badge tone={FILE_TONE[f.status] ?? "info"}>{t(`drive.file.${f.status}` as never)}</Badge>
              {f.note ? <span className="hint"> {f.note}</span> : null}
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}
